import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Sparkles, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { BrandMark } from "@/components/BrandLogo";

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [desc, setDesc] = useState("");
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState("");
  const { refresh, current } = useOrg();
  const nav = useNavigate();

  const build = async () => {
    if (!desc.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding/build", { description: desc });
      setRec(data);
      setCompany(current?.org?.name?.replace("'s Business", "") || "");
      setStep(1);
    } catch (e) { toast.error("Could not analyse — try again"); }
    finally { setLoading(false); }
  };

  const skip = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { industry: "general", company_name: "My Workspace" });
      await refresh();
      nav("/app/chat/new");
    } catch (e) { toast.error("Could not skip"); }
    finally { setLoading(false); }
  };

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { industry: rec.industry, modules: rec.modules, company_name: company || "My Company" });
      await refresh();
      toast.success("Workspace ready");
      nav("/app/dashboard");
    } catch (e) { toast.error("Setup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex h-full items-center justify-center overflow-y-auto bg-black diamond-radial px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandMark size={60} glow />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight">Build My Business Workspace</h1>
          <p className="mt-2 text-white/45">Tell BDVG AI about your business — it configures the rest.</p>
        </div>

        {step === 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl border border-white/10 p-6">
            <label className="mb-2 block text-sm text-white/60">Describe your business</label>
            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} autoFocus
              placeholder="e.g. I run a domestic cleaning company with 12 cleaners covering North London."
              className="w-full resize-none rounded-xl border border-white/10 bg-graphite px-4 py-3 text-sm focus:border-gold/40 focus:outline-none" data-testid="onboarding-desc" />
            <button onClick={build} disabled={loading || !desc.trim()} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold py-3 text-sm font-semibold text-black disabled:opacity-40" data-testid="onboarding-build">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4" /> Build My Workspace</>}
            </button>
            <button onClick={skip} className="mt-3 w-full text-center text-xs text-white/45 hover:text-gold" data-testid="onboarding-skip">
              I'll use BDVG AI for personal work — skip for now
            </button>
          </motion.div>
        )}

        {step === 1 && rec && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl border border-gold/25 p-6">
            <p className="text-sm text-white/50">Recommended workspace</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-gradient-gold">{rec.label}</h2>
            <p className="mt-1 text-sm text-white/60">{rec.summary}</p>
            <div className="mt-4">
              <p className="mb-2 text-xs uppercase tracking-wider text-white/40">Modules to enable</p>
              <div className="flex flex-wrap gap-2">
                {rec.modules.map((m) => (
                  <span key={m} className="flex items-center gap-1 rounded-full border border-gold/25 bg-gold/5 px-3 py-1 text-xs capitalize text-gold/85"><Check className="h-3 w-3" /> {m}</span>
                ))}
              </div>
            </div>
            <label className="mt-5 mb-2 block text-sm text-white/60">First company name</label>
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" data-testid="onboarding-company"
              className="w-full rounded-xl border border-white/10 bg-graphite px-4 py-3 text-sm focus:border-gold/40 focus:outline-none" />
            <div className="mt-5 flex gap-2">
              <button onClick={() => setStep(0)} className="rounded-xl border border-white/10 px-4 py-3 text-sm text-white/60">Back</button>
              <button onClick={finish} disabled={loading} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold py-3 text-sm font-semibold text-black disabled:opacity-40" data-testid="onboarding-finish">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Complete Setup <ArrowRight className="h-4 w-4" /></>}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
